// Glance · Plot — the Glance chart with real axes. Ours, not the design system's: it sits beside
// the vendored bundle (glance.js) and draws with that bundle's own helpers and colour tokens, so it
// reads as one more Glance chart. A pure function like the rest: options in, SVG string out.
//
// The card charts leave out everything a reader needs to take an exact figure off a chart. Each
// of those is an option here, so a caller (or a report's chart settings) turns it off:
//   axes, grid, legend (2+ series), values (figures on marks, where they fit), zero (value axis
//   from zero) — all on by default.
//   horizontal (bars sideways), stacked, log (log value axis), area (lines filled) — off.
//   series[]: { name, data, type: bar|line|area, axis: 'right', stack, color } — any number of
//   series; `axis: 'right'` draws one on a second value axis, formatted by `rightFormat`.
//   refs: [{ value, label, axis }] and target/targetLabel draw dashed reference lines.
//   zoom: drag across the plot to zoom into those categories; `window: [from, to]` is the range.
//   drill: { "<category>": childSpec } makes that category clickable; `path` lists the ones
//   clicked so far. window and path are view state — interact() keeps them for a host.
import "./glance.js"

const G = window.Glance
const { col, esc, r2, clamp, D, tw, fit, tip, T, open, rr, rect, stroke, FMT, SERIES } = G.kit

const DEFAULTS = {
  categories: [], series: [], width: 320, height: 220, rowH: 26,
  axes: true, grid: true, legend: true, values: true, zero: true,
  horizontal: false, stacked: false, log: false, area: false,
  refs: null, target: null, targetLabel: null, color: null, format: null, rightFormat: null,
  zoom: false, window: null, drill: null, path: null, label: null,
}
// A drilled child brings its own data; everything else (format, stacked, log…) carries down.
const OWN = ["categories", "series", "drill"]
const RADIUS = 3

function num(v) {
  if (v == null || v === "") return null
  const n = +v
  return Number.isFinite(n) ? n : null
}
const clean = (v) => +v.toPrecision(12)

function niceStep(raw) {
  const p = Math.pow(10, Math.floor(Math.log10(raw))), f = raw / p
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p
}

// About n intervals; exactly n when `fixed`, so a second axis lands on the first one's gridlines.
function linear(lo, hi, n, fixed) {
  if (hi === lo) { hi += 1; lo -= 1 }
  let step = niceStep((hi - lo) / n), a, b
  for (let guard = 0; guard < 40; guard++) {
    a = Math.floor(lo / step + 1e-9) * step
    b = Math.ceil(hi / step - 1e-9) * step
    if (!fixed || Math.round((b - a) / step) <= n) break
    step = niceStep(step * 1.0001)
  }
  if (fixed) b = a + n * step
  const ticks = []
  for (let i = 0, k = Math.round((b - a) / step); i <= k; i++) ticks.push(clean(a + i * step))
  return { lo: a, hi: b, ticks, at: (v) => (v - a) / (b - a) }
}

// Decades. Nothing at or below zero has a place on it, so those marks are left out.
function logarithmic(lo, hi) {
  const a = Math.floor(Math.log10(lo)), b = Math.max(a + 1, Math.ceil(Math.log10(hi))), every = Math.ceil((b - a) / 6), ticks = []
  for (let e = a; e <= b; e += every) ticks.push(clean(Math.pow(10, e)))
  return { lo: Math.pow(10, a), hi: Math.pow(10, b), ticks, log: true, at: (v) => (v > 0 ? (Math.log10(v) - a) / (b - a) : null) }
}

function resolve(o) {
  let spec = o
  const crumbs = []
  for (const key of o.path || []) {
    const child = spec.drill && typeof spec.drill === "object" ? spec.drill[key] : null
    if (!child) break
    const base = {}
    for (const k in spec) if (!OWN.includes(k)) base[k] = spec[k]
    spec = D(child, base)
    crumbs.push(key)
  }
  return [spec, crumbs]
}

function Plot(options) {
  const o = D(options, DEFAULTS)
  const [spec, crumbs] = resolve(o)
  const W = o.width, f = FMT(spec.format), fr = FMT(spec.rightFormat || spec.format)
  const all = (spec.categories || []).map(String)

  let w0 = 0, w1 = all.length - 1
  if (Array.isArray(o.window) && all.length) {
    w0 = clamp(Math.round(o.window[0]), 0, all.length - 1)
    w1 = clamp(Math.round(o.window[1]), w0, all.length - 1)
  }
  const zoomed = w0 > 0 || w1 < all.length - 1
  const cats = all.slice(w0, w1 + 1), n = cats.length

  const series = (spec.series || []).map((s, i) => {
    let type = s.type === "line" || s.type === "area" ? s.type : "bar"
    if (spec.area && type === "line") type = "area"
    return {
      name: s.name == null ? `Series ${i + 1}` : String(s.name),
      data: (s.data || []).slice(w0, w1 + 1).map(num),
      type, right: s.axis === "right",
      stack: s.stack != null ? String(s.stack) : spec.stacked && type !== "line" ? "all" : null,
      color: s.color || (i === 0 && spec.color) || SERIES[i % SERIES.length],
    }
  })
  if (!n || !series.length) return open(W, 40, "No data") + T(0, 20, "No data", { sans: 1, size: 12 }) + "</svg>"
  // A second axis needs a first one.
  if (series.every((s) => s.right)) series.forEach((s) => (s.right = false))
  const dual = series.some((s) => s.right)
  const horizontal = spec.horizontal && series.every((s) => s.type === "bar")
  const label = o.label || `${series.map((s) => s.name).join(", ")} across ${n} ${n === 1 ? "category" : "categories"}`
  const fmtOf = (s) => (s.right ? fr : f)

  // Stacks: positives climb from zero and negatives fall from it, separately, per category.
  const stacks = {}
  series.forEach((s, si) => {
    s.key = s.stack == null ? null : (s.right ? "r:" : "l:") + s.stack
    s.a = []; s.b = []
    s.data.forEach((v, i) => {
      if (v == null || s.key == null) { s.a[i] = null; s.b[i] = v; return }
      const st = stacks[s.key] || (stacks[s.key] = { pos: [], neg: [], top: { pos: [], neg: [] } })
      const side = v < 0 ? "neg" : "pos", from = st[side][i] || 0
      s.a[i] = from; s.b[i] = from + v; st[side][i] = from + v; st.top[side][i] = si
    })
  })
  const outermost = (s, si, i) => s.key == null || stacks[s.key].top[s.data[i] < 0 ? "neg" : "pos"][i] === si

  const refs = (spec.refs || []).concat(spec.target != null ? [{ value: spec.target, label: spec.targetLabel }] : [])
    .map((r) => ({ value: num(r.value), label: r.label, right: dual && r.axis === "right" })).filter((r) => r.value != null)

  function scaleFor(right, ticks, fixed, log) {
    const vals = [], own = series.filter((s) => s.right === right)
    if (!own.length) return null
    own.forEach((s) => s.b.forEach((v, i) => { if (v != null) vals.push(v, s.a[i] ?? v) }))
    refs.forEach((r) => r.right === right && vals.push(r.value))
    if (!vals.length) vals.push(0)
    if (log) {
      const pos = vals.filter((v) => v > 0)
      if (pos.length) return logarithmic(Math.min(...pos), Math.max(...pos))
    }
    let lo = Math.min(...vals), hi = Math.max(...vals)
    // A bar's length is its value, so a bar axis always starts at zero; a line's may not.
    if (spec.zero || own.some((s) => s.type !== "line")) { lo = Math.min(lo, 0); hi = Math.max(hi, 0) }
    return linear(lo, hi, ticks, fixed)
  }
  const scales = (ticks) => {
    const L = scaleFor(false, ticks, false, spec.log)
    const R = dual ? scaleFor(true, L.log ? ticks : L.ticks.length - 1, !L.log, false) : null
    return [L, R]
  }
  const baseOf = (sc) => (sc.log ? sc.lo : clamp(0, sc.lo, sc.hi))

  // ---------- header: drill crumbs, zoom reset ----------
  let out = "", y = 0
  if (crumbs.length || zoomed) {
    let x = 0
    if (crumbs.length) {
      ["All"].concat(crumbs).forEach((part, depth) => {
        const last = depth === crumbs.length, text = fit(part, 180, 11.5)
        out += last ? T(x, 12, text, { sans: 1, size: 11.5, weight: 500, color: "ink" })
          : `<g data-gl-depth="${depth}" role="button" tabindex="0" style="cursor:pointer">${T(x, 12, text, { sans: 1, size: 11.5, color: "data-blue" })}</g>`
        x += tw(text, 11.5) + 6
        if (!last) { out += T(x, 12, "›", { sans: 1, size: 11.5 }); x += 12 }
      })
    }
    if (zoomed) {
      out += T(crumbs.length ? x + 10 : 0, 12, `${all[w0]} – ${all[w1]} · ${n} of ${all.length}`, { size: 10.5 })
      out += `<g data-gl-reset role="button" tabindex="0" style="cursor:pointer">${T(W, 12, "Reset zoom", { sans: 1, size: 11.5, color: "data-blue", anchor: "end" })}</g>`
    }
    y = 22
  }

  // ---------- legend ----------
  if (spec.legend !== false && series.length > 1) {
    let lx = 0, row = 0
    series.forEach((s) => {
      const name = s.name + (s.right ? " (right axis)" : ""), w = 14 + tw(name, 11.5) + 16
      if (lx + w > W && lx > 0) { lx = 0; row++ }
      const ly = y + row * 18 + 11
      out += s.type === "line" ? `<path d="M${r2(lx)},${r2(ly - 4)}h9"${stroke(s.color, 2)}/>` : rect(lx, ly - 8.5, 9, 9, 2, s.color)
      out += T(lx + 14, ly, name, { sans: 1, size: 11.5, color: "ink-2" })
      lx += w
    })
    y += (row + 1) * 18 + 4
  }

  const marks = []      // drawn under the hover bands, which take the pointer
  const bandTip = (i) => {
    const c = cats[i], parts = series.filter((s) => s.data[i] != null).map((s) => `${s.name} ${fmtOf(s)(s.data[i])}`)
    const can = spec.drill && typeof spec.drill === "object" && spec.drill[c]
    return tip([c].concat(parts, can ? ["Click to break down"] : []).join("\n")) +
      (can ? ` data-gl-drill="${esc(c)}" role="button" tabindex="0" style="cursor:pointer"` : "")
  }
  const bars = series.filter((s) => s.type === "bar"), groups = []
  bars.forEach((s) => { const g = s.key || s.name + series.indexOf(s); if (!groups.includes(g)) groups.push(g); s.group = groups.indexOf(g) })
  const gN = groups.length
  const shown = (s, i) => (s.key == null ? s.data[i] : stacks[s.key].pos[i] ?? s.data[i])

  let H
  if (horizontal) {
    // ---------- sideways: categories down the left, values along the bottom ----------
    const rowH = spec.rowH, labelW = Math.min(Math.max(...cats.map((c) => tw(c, 12))) + 14, W * 0.38)
    const valW = spec.values && gN === 1 ? Math.max(...bars.flatMap((s) => s.data.map((v, i) => (v == null ? 0 : tw(fmtOf(s)(shown(s, i)), 10.5, 1))))) + 10 : 8
    const top = y + (spec.axes && dual ? 18 : 4), bot = top + n * rowH
    H = bot + (spec.axes ? 18 : 4)
    const x0 = labelW, x1 = Math.max(x0 + 40, W - valW)
    const [L, R] = scales(clamp(Math.round((x1 - x0) / 90), 2, 6))
    const hx = (sc, v) => { const t = sc.at(v); return t == null ? null : x0 + t * (x1 - x0) }
    L.ticks.forEach((t) => {
      const x = hx(L, t)
      if (spec.grid) out += `<path d="M${r2(x)},${r2(top)}V${r2(bot)}"${stroke(t === 0 ? "line" : "grid", 1, ";stroke-linecap:butt")}/>`
      if (spec.axes) out += T(x, H - 4, f(t), { anchor: "middle" })
    })
    if (R && spec.axes) R.ticks.forEach((t) => (out += T(hx(R, t), top - 6, fr(t), { anchor: "middle" })))
    const gh = Math.min(16, (rowH * 0.66) / Math.max(gN, 1)), bh = gh * (gN > 1 ? 0.86 : 1)
    cats.forEach((c, i) => {
      out += `<rect x="0" y="${r2(top + i * rowH)}" width="${r2(W)}" height="${rowH}" class="gl-band" fill="transparent"${bandTip(i)}/>`
      if (spec.axes) marks.push(T(0, top + i * rowH + rowH / 2 + 4, fit(c, labelW - 10, 12), { sans: 1, size: 12, color: "ink-2" }))
    })
    bars.forEach((s) => {
      const si = series.indexOf(s), sc = s.right ? R : L
      s.b.forEach((v, i) => {
        if (v == null) return
        const xa = hx(sc, s.a[i] ?? baseOf(sc)), xb = hx(sc, v)
        if (xa == null || xb == null) return
        const yb = top + i * rowH + (rowH - gN * gh) / 2 + s.group * gh + (gh - bh) / 2, right = xb >= xa
        const rad = outermost(s, si, i) ? RADIUS : 0, left = Math.min(xa, xb), w = Math.max(1.5, Math.abs(xb - xa))
        marks.push(`<path d="${rr(left, yb, w, bh, right ? 0 : rad, right ? rad : 0, right ? rad : 0, right ? 0 : rad)}" style="fill:${col(s.color)}${s.key ? `;stroke:${col("card")};stroke-width:1` : ""}"/>`)
        if (spec.values && gN === 1 && outermost(s, si, i) && (s.key == null || s.data[i] >= 0)) {
          const total = shown(s, i), xt = hx(sc, total) ?? xb
          marks.push(T(total < 0 ? xt - 5 : xt + 5, yb + bh / 2 + 3.5, fmtOf(s)(total), { anchor: total < 0 ? "end" : "start", color: "ink" }))
        }
      })
    })
    refs.forEach((r) => {
      const x = hx(r.right ? R : L, r.value)
      if (x == null) return
      marks.push(`<path d="M${r2(x)},${r2(top - 2)}V${r2(bot)}"${stroke("ink-3", 1, ";stroke-dasharray:4 3;stroke-linecap:butt")}/>`)
      marks.push(T(x + 4, top + 8, r.label || (r.right ? fr : f)(r.value), { size: 10, color: "ink-2" }))
    })
  } else {
    // ---------- upright: categories along the bottom ----------
    const lines = series.filter((s) => s.type !== "bar")
    const ends = spec.values && !dual && lines.length && lines.length <= 6
    const top = y + (spec.values && gN ? 14 : 8), foot = spec.axes ? 20 : 4
    H = Math.max(spec.height, top + 60 + foot)
    const bot = H - foot
    const [L, R] = scales(clamp(Math.round((bot - top) / 45), 2, 6))
    const lw = spec.axes ? Math.max(...L.ticks.map((t) => tw(f(t), 10.5, 1))) + 8 : 0
    const rw = spec.axes && R ? Math.max(...R.ticks.map((t) => tw(fr(t), 10.5, 1))) + 8 : 0
    const endW = ends ? Math.max(...lines.map((s) => { const v = s.data.filter((x) => x != null).pop(); return v == null ? 0 : tw(f(v), 10.5, 1) })) + 12 : 4
    const x0 = lw, x1 = Math.max(x0 + 40, W - rw - endW), slot = (x1 - x0) / n
    const vy = (sc, v) => { const t = sc.at(v); return t == null ? null : bot - t * (bot - top) }
    const cx = (i) => x0 + slot * (i + 0.5)

    L.ticks.forEach((t) => {
      const yy = vy(L, t)
      if (spec.grid) out += `<path d="M${r2(x0)},${r2(yy)}H${r2(x1)}"${stroke("grid", 1, ";stroke-linecap:butt")}/>`
      if (spec.axes) out += T(x0 - 6, yy + 3.5, f(t), { anchor: "end" })
    })
    if (R && spec.axes) R.ticks.forEach((t) => (out += T(x1 + 6, vy(R, t) + 3.5, fr(t))))
    out += `<path d="M${r2(x0)},${r2(vy(L, baseOf(L)) + 0.5)}H${r2(x1)}"${stroke("line", 1, ";stroke-linecap:butt")}/>`

    const plot = spec.zoom && n > 2 ? ` data-gl-plot="${[x0, x1, top, bot, w0, n].map(r2).join(",")}" style="cursor:crosshair"` : ""
    out += `<g${plot}>`
    cats.forEach((c, i) => (out += `<rect x="${r2(x0 + slot * i)}" y="${r2(top)}" width="${r2(slot)}" height="${r2(bot - top)}" class="gl-band" fill="transparent"${bandTip(i)}/>`))
    out += "</g>"

    // Areas first, so bars and lines sit on them.
    lines.filter((s) => s.type === "area").forEach((s) => {
      const sc = s.right ? R : L
      let run = []
      const flush = () => {
        if (run.length > 1) {
          const upper = run.map((i) => `${r2(cx(i))},${r2(vy(sc, s.b[i]))}`), lower = run.slice().reverse().map((i) => `${r2(cx(i))},${r2(vy(sc, s.a[i] ?? baseOf(sc)))}`)
          marks.push(`<path d="M${upper.join("L")}L${lower.join("L")}Z" style="fill:${col(s.color)};fill-opacity:.14"/>`)
        }
        run = []
      }
      s.b.forEach((v, i) => (v == null || vy(sc, v) == null ? flush() : run.push(i)))
      flush()
    })

    const gw = gN ? Math.min(24 / 0.86, (slot * 0.72) / gN) : 0, bw = gw * (gN > 1 ? 0.86 : 1)
    const labelled = spec.values && gN === 1 && bars.every((s) => s.data.every((v, i) => v == null || tw(fmtOf(s)(shown(s, i)), 10, 1) <= slot - 2))
    bars.forEach((s) => {
      const si = series.indexOf(s), sc = s.right ? R : L
      s.b.forEach((v, i) => {
        if (v == null) return
        const ya = vy(sc, s.a[i] ?? baseOf(sc)), yb = vy(sc, v)
        if (ya == null || yb == null) return
        const x = x0 + slot * i + (slot - gN * gw) / 2 + s.group * gw + (gw - bw) / 2, up = yb <= ya
        const rad = outermost(s, si, i) ? RADIUS : 0, y1 = Math.min(ya, yb), h = Math.max(1.5, Math.abs(ya - yb))
        marks.push(`<path d="${rr(x, y1, bw, h, up ? rad : 0, up ? rad : 0, up ? 0 : rad, up ? 0 : rad)}" style="fill:${col(s.color)}${s.key ? `;stroke:${col("card")};stroke-width:1` : ""}"/>`)
        if (labelled && outermost(s, si, i) && (s.key == null || s.data[i] >= 0)) {
          const total = shown(s, i), yt = vy(sc, total) ?? yb
          marks.push(T(x + bw / 2, total < 0 ? yt + 12 : yt - 4, fmtOf(s)(total), { anchor: "middle", size: 10, color: "ink" }))
        }
      })
    })

    const endLabels = []
    lines.forEach((s) => {
      const sc = s.right ? R : L
      const pts = s.b.map((v, i) => { const yy = v == null ? null : vy(sc, v); return yy == null ? null : [cx(i), yy] })
      let d = "", pen = false
      pts.forEach((p) => { if (!p) { pen = false; return } d += (pen ? "L" : "M") + r2(p[0]) + "," + r2(p[1]); pen = true })
      marks.push(`<path d="${d}"${stroke(s.color, 2)}/>`)
      // Dots while the points are few enough to read as points; past that they crowd the line.
      if (n <= 12 && lines.length <= 3) pts.forEach((p) => p && marks.push(`<circle cx="${r2(p[0])}" cy="${r2(p[1])}" r="2.5" style="fill:${col(s.color)};stroke:${col("card")};stroke-width:1.5"/>`))
      const last = pts.findLastIndex((p) => p)
      if (ends && last >= 0) endLabels.push({ x: pts[last][0] + 7, y: pts[last][1] + 3.5, y0: pts[last][1] + 3.5, text: f(s.data[last]), color: s.color })
    })
    // The last figure is the one a trend is read for: at the end of its own line, nudged apart.
    endLabels.sort((a, b) => a.y - b.y).forEach((e, i, arr) => { if (i && e.y - arr[i - 1].y < 13) e.y = arr[i - 1].y + 13 })
    endLabels.forEach((e) => marks.push(T(e.x, e.y, e.text, { size: 10.5, weight: 600, color: e.color })))

    refs.forEach((r) => {
      const yy = vy(r.right ? R : L, r.value)
      if (yy == null) return
      marks.push(`<path d="M${r2(x0)},${r2(yy)}H${r2(x1)}"${stroke("ink-3", 1, ";stroke-dasharray:4 3;stroke-linecap:butt")}/>`)
      marks.push(T(x1 - 2, yy - 4, r.label || (r.right ? fr : f)(r.value), { anchor: "end", size: 10, color: "ink-2" }))
    })

    if (spec.axes) {
      // Every k-th name, so none collide; the band's tooltip still names each one.
      const widest = Math.min(Math.max(...cats.map((c) => tw(c, 10.5, 1))), 120), every = Math.max(1, Math.ceil((widest + 8) / slot))
      cats.forEach((c, i) => {
        if (i % every) return
        const text = fit(c, slot * every - 6, 10.5, 1), w = tw(text, 10.5, 1)
        out += T(clamp(cx(i), w / 2, W - w / 2), H - 5, text, { anchor: "middle" })
      })
    }
  }

  return open(W, H, label) + out + `<g style="pointer-events:none">${marks.join("")}</g></svg>`
}

// Zoom and drill for a host that redraws: the host keeps the view ({ window, path }), merges it
// into the chart's options, and redraws when setView hands it a new one.
function interact(el, getView, setView) {
  if (el.__glInteract) return
  el.__glInteract = true
  let drag = null, swallowUntil = 0
  const go = (patch) => setView({ ...(getView() || {}), ...patch })
  const at = (svg, e) => { const r = svg.getBoundingClientRect(), b = svg.viewBox.baseVal; return ((e.clientX - r.left) * b.width) / (r.width || 1) }

  el.addEventListener("pointerdown", (e) => {
    const g = e.target.closest?.("[data-gl-plot]")
    if (!g || e.button !== 0) return
    const [x0, x1, y0, y1, first, count] = g.getAttribute("data-gl-plot").split(",").map(Number)
    drag = { svg: g.ownerSVGElement, from: at(g.ownerSVGElement, e), x0, x1, y0, y1, first, count, box: null }
    el.setPointerCapture?.(e.pointerId)
    e.preventDefault()
  })
  el.addEventListener("pointermove", (e) => {
    if (!drag) return
    const x = clamp(at(drag.svg, e), drag.x0, drag.x1)
    if (!drag.box && Math.abs(x - drag.from) < 4) return
    if (!drag.box) {
      drag.box = document.createElementNS("http://www.w3.org/2000/svg", "rect")
      drag.box.setAttribute("style", `fill:${col("data-blue")};fill-opacity:.12;pointer-events:none`)
      drag.svg.appendChild(drag.box)
    }
    const a = Math.min(x, drag.from)
    Object.entries({ x: a, y: drag.y0, width: Math.abs(x - drag.from), height: drag.y1 - drag.y0 }).forEach(([k, v]) => drag.box.setAttribute(k, r2(v)))
  })
  const finish = (e, apply) => {
    const d = drag
    drag = null
    if (!d?.box) return
    d.box.remove()
    swallowUntil = performance.now() + 300
    if (!apply) return
    const slot = (d.x1 - d.x0) / d.count, x = clamp(at(d.svg, e), d.x0, d.x1)
    const idx = (px) => clamp(Math.floor((px - d.x0) / slot), 0, d.count - 1)
    const i0 = idx(Math.min(x, d.from)), i1 = idx(Math.max(x, d.from))
    if (i1 > i0) go({ window: [d.first + i0, d.first + i1] })
  }
  el.addEventListener("pointerup", (e) => finish(e, true))
  el.addEventListener("pointercancel", (e) => finish(e, false))

  const act = (target) => {
    const drill = target.closest?.("[data-gl-drill]"), up = target.closest?.("[data-gl-depth]"), reset = target.closest?.("[data-gl-reset]")
    if (drill) go({ path: [...(getView()?.path || []), drill.getAttribute("data-gl-drill")], window: null })
    else if (up) go({ path: (getView()?.path || []).slice(0, +up.getAttribute("data-gl-depth")), window: null })
    else if (reset) go({ window: null })
    else return false
    return true
  }
  el.addEventListener("click", (e) => { if (performance.now() > swallowUntil) act(e.target) })
  el.addEventListener("keydown", (e) => { if ((e.key === "Enter" || e.key === " ") && act(e.target)) e.preventDefault() })
}

G.Plot = Plot
G.interact = interact
